
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ReadyToStartSection from '../../components/feature/ReadyToStartSection';
import Button from '../../components/base/Button';

export default function Programs() {
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  const programs = [
    {
      id: 1,
      title: 'Infants',
      age: '6 weeks - 12 months',
      description: 'Our infant curriculum focuses on nurturing your baby\'s early development through responsive care, sensory exploration, and secure attachments. Teachers support cognitive and motor growth through tummy time, music, stories, and gentle interaction. We provide a safe, loving environment where infants can explore at their own pace and begin building foundational skills for lifelong learning.',
      icon: 'ri-heart-3-line',
      image: 'https://public.readdy.ai/ai/img_res/49cf332e18454124fe6172921e38462b.jpg'
    },
    {
      id: 2,
      title: 'Toddlers',
      age: '12 months - 2 years',
      description: 'Our toddler program encourages curiosity, independence, and language development through hands-on play and daily routines. Activities like music, movement, sensory play, and early social interaction help toddlers develop communication, motor skills, and self-awareness. We foster a warm and structured environment that supports toddlers as they begin to express themselves and explore the world around them.',
      icon: 'ri-run-line',
      image: 'https://readdy.ai/api/search-image?query=Toddlers%20playing%20with%20colorful%20educational%20toys%20in%20bright%20modern%20classroom%2C%20children%20exploring%20and%20learning%20together%2C%20professional%20daycare%20environment%20with%20safety%20features&width=400&height=300&seq=toddler1&orientation=landscape'
    },
    {
      id: 3,
      title: 'Preschool',
      age: '2 - 4 years',
      description: 'Our preschool curriculum promotes early learning through play-based experiences that build skills in language, math, science, and social-emotional development. Children engage in creative activities, group play, and structured routines that encourage independence and curiosity. We focus on developing the whole child in a fun, nurturing setting that supports each child\'s unique pace and interests.',
      icon: 'ri-palette-line',
      image: 'https://readdy.ai/api/search-image?query=Preschool%20children%20engaged%20in%20creative%20art%20activities%20and%20learning%20games%2C%20bright%20colorful%20classroom%20with%20educational%20materials%2C%20teachers%20guiding%20children%20in%20structured%20play&width=400&height=300&seq=preschool1&orientation=landscape'
    },
    {
      id: 4,
      title: 'Pre-K',
      age: '4 - 5 years',
      description: 'Our Pre-K curriculum is designed to prepare children for kindergarten through hands-on learning and school-readiness activities. We focus on early literacy, math, problem-solving, and collaboration, while also fostering creativity and confidence. With a balance of structured lessons and exploratory play, our program helps children transition smoothly into the next stage of their education.',
      icon: 'ri-graduation-cap-line',
      image: 'https://readdy.ai/api/search-image?query=Pre-K%20children%20in%20advanced%20learning%20activities%2C%20STEM%20education%2C%20reading%20and%20writing%20preparation%2C%20modern%20classroom%20with%20educational%20technology%20and%20learning%20centers&width=400&height=300&seq=prek1&orientation=landscape'
    }
  ];

  useEffect(() => {
    const observers = cardsRef.current.map((card, index) => {
      if (!card) return null;
      
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              setVisibleCards(prev => [...prev, index]);
            }, index * 120);
          }
        },
        { threshold: 0.2 }
      );
      
      observer.observe(card);
      return observer;
    });

    return () => {
      observers.forEach(observer => observer?.disconnect());
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#FFF7E8]">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-[#333333] mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Our Programs
          </h1>
          <p className="text-xl text-[#333333]/80 max-w-3xl mx-auto mb-8" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            Age-appropriate learning experiences designed to nurture every child's unique journey of growth and discovery.
          </p>
        </div>
      </section>

      {/* Programs Grid */}
      <section className="pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {programs.map((program, index) => (
              <div
                key={program.id}
                ref={el => cardsRef.current[index] = el}
                className={`bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-500 hover:shadow-xl hover:-translate-y-1 cursor-pointer ${
                  visibleCards.includes(index) 
                    ? 'translate-y-0 opacity-100' 
                    : 'translate-y-5 opacity-0'
                }`}
              >
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={program.image} 
                    alt={program.title}
                    className="w-full h-full object-cover object-top transition-transform duration-300 hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 bg-[#A8D8F0] text-white px-3 py-1 rounded-full text-sm font-medium">
                    {program.age}
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#A8D8F0]/10 rounded-full mr-4 transition-colors duration-150 hover:bg-[#A8D8F0]/20">
                      <i className={`${program.icon} text-2xl text-[#A8D8F0] transition-colors duration-150 hover:text-[#BFE6CC]`}></i>
                    </div>
                    <h3 className="text-2xl font-bold text-[#333333]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {program.title}
                    </h3>
                  </div>
                  
                  <p className="text-[#333333]/80 mb-6 leading-relaxed" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    {program.description}
                  </p>
                  
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => {
                      if (window.REACT_APP_NAVIGATE) {
                        window.REACT_APP_NAVIGATE('/contact');
                      }
                    }}
                  >
                    Book a Tour to Learn More
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Programs Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Additional Learning Opportunities
            </h2>
            <p className="text-lg text-[#333333]/80 max-w-2xl mx-auto" style={{ fontFamily: 'Open Sans, sans-serif' }}>
              Enriching experiences that complement our core programs
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6 rounded-xl bg-[#A8D8F0]/5 hover:bg-[#A8D8F0]/10 transition-colors duration-200">
              <div className="w-16 h-16 flex items-center justify-center bg-[#A8D8F0] rounded-full mx-auto mb-4">
                <i className="ri-book-open-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-[#333333] mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Curriculum & Daily Schedule
              </h3>
              <p className="text-[#333333]/80 mb-4" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                Discover our play-based learning approach and daily routines
              </p>
              <Link 
                to="/programs/curriculum" 
                className="text-[#A8D8F0] hover:text-[#BFE6CC] font-medium transition-colors duration-200"
              >
                Learn More →
              </Link>
            </div>
            
            <div className="text-center p-6 rounded-xl bg-[#BFE6CC]/5 hover:bg-[#BFE6CC]/10 transition-colors duration-200">
              <div className="w-16 h-16 flex items-center justify-center bg-[#BFE6CC] rounded-full mx-auto mb-4">
                <i className="ri-camera-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-[#333333] mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Gallery
              </h3>
              <p className="text-[#333333]/80 mb-4" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                See our facilities and children in action through photos
              </p>
              <Link 
                to="/gallery" 
                className="text-[#BFE6CC] hover:text-[#A8D8F0] font-medium transition-colors duration-200"
              >
                View Gallery →
              </Link>
            </div>
            
            <div className="text-center p-6 rounded-xl bg-[#A8D8F0]/5 hover:bg-[#A8D8F0]/10 transition-colors duration-200">
              <div className="w-16 h-16 flex items-center justify-center bg-[#A8D8F0] rounded-full mx-auto mb-4">
                <i className="ri-calendar-check-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-semibold text-[#333333] mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Schedule a Visit
              </h3>
              <p className="text-[#333333]/80 mb-4" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                Book a personalized tour to see our programs firsthand
              </p>
              <Link 
                to="/contact" 
                className="text-[#A8D8F0] hover:text-[#BFE6CC] font-medium transition-colors duration-200"
              >
                Schedule Tour →
              </Link>
            </div>
          </div>
        </div>
      </section>

      <ReadyToStartSection />
      <Footer />
    </div>
  );
}
